(function() {
  // Create namespaced logger
  const Logger = window.ShopLogger ? 
    window.ShopLogger.createLogger('CartCalculator') : 
    { error: console.error, warn: console.warn, info: () => {}, debug: () => {} };

  window.cartCalculator = {
    async calculateCart(props = {}) {
      // Check if customer is logged in
      const isLoggedIn = window.kitSelector.shopifyCustomerId && 
                       window.kitSelector.shopifyCustomerId !== '' && 
                       window.kitSelector.shopifyCustomerId !== null;
      
      // If not logged in, skip calculation and return success
      if (!isLoggedIn) {
        Logger.debug('Customer not logged in, skipping calculate-order');
        return { ok: true, status: 200 };
      }
      
      // Existing validation for logged-in customers
      if (!this.validateCustomerInfo()) {
        return false;
      }
    /**
      Props is an object that can be empty, or contain three different payloads that need to be sent to calculate-order:
      1. A single simple product with the shape: { variantId, parentProductSku, qty }
      2. An array of simple products with the shape { items: [{ variantId, parentProductSku, qty }, ...] }
      3. A single kit (never passed with multiple items) with the shape
      { 
        kit: {
          string: { // the group key
            "groupQuantity": number,
            "products": {
              "9102102": {
                "item": {
                  ...shopify product data
                },
                "quantity": number
              }
            },
            "is_free": boolean
          },
          ...string: {
            ...may contain multiple groups
          }
        }, 
        parentProductSku: string, 
        variantId: number, 
        parentProductId: number, 
        qty: 1, // always 1
        hiddenGiftSkus: [{
          "item": {}, // always empty
          "quantity": number,
          "sku": string,
          "groupKey": string
        }] 
      }
      When calculate-cart is called without props, it means that the cart is being calculated without 
      adding a new item. For example, when the user removes an item from the cart. 

      In the case of a kit, cartCalculator handles sending products to the shopify cart. For cases 1 and 2,
      adding to cart is handled by the respective components calling calculateCart().
    */

      this.props = props;

      this.hasProps = this.props && Object.keys(this.props).length > 0;
      this.cartItems = null; 
      this.formattedNewItem = null;
      if (!this.props.kit || Object.keys(props.kit).length === 0) {
        this.isKit = false;
      } else {
        this.isKit = true;
      }
      this.newItemInCart = false;

      // Build the request object
      const request = {
        webuser_id: window.kitSelector.customerOgasysId.toString().replace(/[^0-9-]/g, ''),
        external_id: window.kitSelector.ogasysExternalId.toString().replace(/[^0-9-]/g, ''),
        shopify_customer_id: window.kitSelector.shopifyCustomerId.toString().replace(/[^0-9-]/g, '')
      };    

      try {
        // gather items already in the cart
        const items = await this.gatherCartItems();

        // format items from the cart into the shape required by the calculate-order API
        const formattedItemsFromCart = this.formatItemsFromCart(items);

        // format the new item from props into the shape required by the calculate-order API
        const formattedNewItem = this.formatNewItems(this.props);
        this.formattedNewItem = formattedNewItem;

        // combine the formatted items from the cart and the new item into a single array
        const formattedItems = this.formRequestItems(formattedItemsFromCart, formattedNewItem);

        // add items to the request object
        request.items = formattedItems; // Add formatted items to request

        // Early return if cart and props are empty, which occurs when last item is removed from cart.
        if (!formattedItems) {
          this.setStorage({}); // Set empty storage
          this.reRenderApp(); // Re-render the app
          return;
        }

        Logger.debug('Request', request);
        const result = await this.callCalculateOrder(request);
        Logger.debug('Result', result);

        if (!result.ok) {
          // Handle errors
          this.setStorage(result);
          if (this.isKit) {
            this.reRenderApp();
            this.handleKitErrors();
          }
          if (!this.isKit) { 
            if (this.hasProps) {
              const errors = [result.body.bo_products, result.body.invalid_products].flatMap((error) => error);
              const errorMessage = this.buildErrorMessage(errors);
              this.showErrorMessage(errorMessage);
            }
          }
          return result;
        }
        this.setStorage(result);
        if (!this.hasProps) {
          return true;
        }
        if (this.isKit) {
          const extractedKit = Array.isArray(this.formattedNewItem) ? this.formattedNewItem[0] : this.formattedNewItem;
          const { kitOptions, kitCartLayout } = this.handleAdditionalItems(extractedKit.options, extractedKit.sku, result.body);
          const _kit_cart_layout = this.buildCartOptions(kitCartLayout);
          
          if (this.newItemInCart) {
            await this.addToCart( this.props.variantId, kitOptions, _kit_cart_layout);
            this.reRenderApp();
          } else {
            await this.updateItemInCart(kitOptions, _kit_cart_layout, this.props.variantId);
            this.reRenderApp();
          }

        } 
        return result;

      } catch (error) {
        Logger.error('Error calculating cart', error);
      }
    },
    validateCustomerInfo() {
      const missingFields = [];
    
      const isMissing = (val) =>
        val === false || val === undefined || val === null || val === '';
    
      if (isMissing(window.kitSelector.shopifyCustomerId)) missingFields.push('shopifyCustomerId');
      if (isMissing(window.kitSelector.customerOgasysId)) missingFields.push('customerOgasysId');
      if (isMissing(window.kitSelector.ogasysExternalId)) missingFields.push('ogasysExternalId');
    
      if (missingFields.length > 0) {
        Logger.error(`Missing required data for calculating the cart: ${missingFields.join(', ')}`);
        return false;
      }
    
      return true;
    },
    //////////////////////////////////////////////////
    // Methods for handling API responses
    ////////////////////////////////////////////////// 
    setStorage(result) {
    
      // Retrieve and parse the object
      let lastItem = JSON.parse(localStorage.getItem('ogasysCart')) || {};
    
      // Ensure `errors` is an object
      if (!lastItem.errors) {
        lastItem.errors = {};
      }
    
      // Update properties
      lastItem.updatedAt = Date.now();
      lastItem.resultSuccessful = result.body?.success || false;
    
      // Extract errors from response
      const invalidProducts = Array.isArray(result.body?.invalid_products) ? result.body.invalid_products : [];
      const boProducts = Array.isArray(result.body?.bo_products) ? result.body.bo_products : [];
    
      lastItem.errors.invalid_products = invalidProducts;
      lastItem.errors.bo_products = boProducts;
    
      // Set options only if errors exist
      const hasErrors = invalidProducts.length > 0 || boProducts.length > 0;
      lastItem.options = hasErrors ? (Array.isArray(this.formattedNewItem.options) ? this.formattedNewItem.options : []) : [];
    
      // Save to localStorage
      localStorage.setItem('ogasysCart', JSON.stringify(lastItem));
    },
    
    reRenderApp() {
      // Check if Alpine is defined and store is available
      if (typeof Alpine === 'undefined' || typeof Alpine.store !== 'function' || Alpine.store('appState').isColorPicker) {
        // Alpine is not defined or store is unavailable. Early returning.
        return;
      }
      Alpine.store('appState').init();
    },
    //////////////////////////////////////////////////
    // Shopify add to cart
    ////////////////////////////////////////////////// 
    async addToCart(variantId, options = [], layout = []) {
      const payload = {
        id: variantId,
        quantity: 1,
      };
    
      if (this.isKit) {
        payload.properties = this.buildProperties(options, layout);
      }
    
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
    
        if (!response.ok) {
          throw new Error('Failed to add item to cart');
        }
    
        const data = await response.json();
    
        // Optionally refresh the cart after all updates are done
        if (typeof window.refreshCart === 'function') {
          await window.refreshCart();
          window.scrollTo(0, 0);
        } else {
          Logger.warn('refreshCart is not defined');
        }
    
        return data; // ✅ important: resolve the data, so await knows it's done
      } catch (error) {
        Logger.error('Error adding to cart', error);
        throw error; // important: propagate errors to the caller
      }
    },
    async updateItemInCart(options, layout, itemId) {
      const properties = this.buildProperties(options, layout);
      
      const currentCartItems = await this.gatherCartItems();
      const cartItemIndex = currentCartItems.findIndex(item => item.id === itemId);

      if (cartItemIndex === -1) {
        return;
      }

      const oneBasedIndex = cartItemIndex + 1; // Convert to 1-based index for Shopify's API
      const payload = {
        line: oneBasedIndex, // ID of the selected variant
        properties: properties,  // Attach grouped items with headers as properties
      };
      // Send the data to Shopify's cart/add.js endpoint
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })
      .then((response) => response.json())
      .then((data) => {

        // Call the refreshCart method to update the cart dynamically (defined in assets/component-cart.js)
        if (typeof window.refreshCart === 'function') {
          window.refreshCart();
          window.scrollTo(0, 0);
        } else {
          Logger.warn('refreshCart is not defined');
        }
      })  
      .catch((error) => {
        Logger.error('Error adding to cart', error);
      });
    },
    buildProperties(options, layout) {
      return {
        "_kit_options": options,
        "_kit_cart_layout": layout,
        "_kit_hidden_gifts": true
      };
    },

    //////////////////////////////////////////////////
    // Methods for handling Gadget Response
    //////////////////////////////////////////////////
    handleKitErrors() { 
      const ogasysCart = JSON.parse(localStorage.getItem('ogasysCart'));
      const cartHasErrors = ogasysCart && !ogasysCart.resultSuccessful;

      if (!cartHasErrors) {
        return; // Exit early
      }
      const errors = [ogasysCart.errors.bo_products, ogasysCart.errors.invalid_products].flatMap((error) => error);
    
      this.scrollToError(errors);
      this.animateErrors(errors);
    },
    scrollToError(errors) {
      const firstErrorElement = document.getElementById(`${errors[0].sku}`);
      const offset = 50;
    
      if (firstErrorElement) {
        const nextTop = firstErrorElement.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: nextTop - offset,
          behavior: "instant"
        }); 
      }
    },
    animateErrors(errors) {
      errors.forEach((error) => {
        const errorElement = document.getElementById(error.sku);
        if (errorElement) {
            // Add glow class
          errorElement.classList.add("glow-error");
      
          // Remove the class after 500ms
          setTimeout(() => {
            errorElement.classList.remove("glow-error");
          }, 1500);
        }
      });
    },
    buildErrorMessage(errors) {
      // This function is for building error messages when the user needs to see
      // the SKU of one or more errored products, ie., with kits multiple simple products
      const themeErrorMsg = errors.length > 1 ? window.kitSelector.productsErrorMessage : window.kitSelector.productErrorMessage;
      const formattedErrors = errors
        .map((error) => error.sku)
        .join(', ');
      const errorMessage = themeErrorMsg
        .replace('{{ errors }}', formattedErrors);

      return errorMessage;
    },
    showErrorMessage(response) {
      // Get the modal element on the page
      const modalElement = document.getElementById('calculate-order-popup');

      // Check if the modal element exists
      if (modalElement) {
        // Update the modal content dynamically
        modalElement.querySelector('[data-content]').textContent =
          response || "An unexpected error occurred. Please try again.";

        // Show the modal
        modalElement.show();
      } else {
        error('Modal element not found on the page.');
      }
    },
    handleAdditionalItems(kitOptions, parentProductSku, response) {
      let kitCartLayout = {};
      if (response.additional.length > 0) {
        kitCartLayout.additionalItems = { products: {}, is_free: false }
      }

      const itemsToRemove = []; // Array to track items to be deleted
      const sku = parentProductSku;

      kitOptions.forEach((kitOption) => {
        kitCartLayout[kitOption.name] = { products: {}, is_free: kitOption.is_free };
        for (const item of kitOption.items) {
          // Add each product to its respective group
          if (!kitCartLayout[kitOption.name].products[item.sku]) {
            kitCartLayout[kitOption.name].products[item.sku] = { item: { name: item.name }, quantity: item.qty };
          }
          // Check to see if the current item matches items in additionalItems
          // Check for matches in response.additional
          const additionalMatch = response.additional.find(
            (additionalItem) =>
              additionalItem.sku === item.sku && additionalItem.parent_sku === sku
          );
          // If match is found, update quantities
          if (additionalMatch) {
            
            // Add to additionalItems bucket
            if (
              !kitCartLayout.additionalItems.products[additionalMatch.sku]
            ) {
              kitCartLayout.additionalItems.products[additionalMatch.sku] = {
                item: { name: item.name },
                quantity: 0,
              };
            }
            kitCartLayout.additionalItems.products[
              additionalMatch.sku
            ].quantity += additionalMatch.qty;
    
            // Decrement quantity in the group
            kitCartLayout[kitOption.name].products[item.sku].quantity -=
              additionalMatch.qty;
            
            // Mark item for removal if quantity is <= 0
            if (
              kitCartLayout[kitOption.name].products[item.sku].quantity <= 0
            ) {
              itemsToRemove.push(item.sku);
            }
            if (item.additionalCount !== additionalMatch.qty) {
              item.additionalCount = additionalMatch.qty;
            }
          }

          // Remove items from groups if they are in the itemsToRemove array
          itemsToRemove.forEach((sku) => {
            delete kitCartLayout[kitOption.name].products[sku];
          });
        }
      });
      return { kitOptions, kitCartLayout };
    },
    buildCartOptions(options) {
      const layout = {};
      for (const groupKey in options) {
        const group = options[groupKey];
        this.buildKitCartLayout(layout, group, groupKey);
      }
      return layout;
    },
    buildKitCartLayout(layout, group, groupKey) {
      const cartFreeItems = window.kitSelector.cartFreeItems;
      const cartAdditionalItems = window.kitSelector.cartAdditionalItems;
      // Use helper to add product details to the group

      // Add the group to the cart layout, which will be displayed in the cart
      if (groupKey === 'additionalItems') { 
        // additional items will always have this flag
        layout[`groupKey: ${groupKey}`] = `${cartAdditionalItems}`;
      } else if (group.is_free) {
        layout[`groupKey: ${groupKey}`] = `${cartFreeItems}`;
      } else {
        layout[`groupKey: ${groupKey}`] = `${groupKey}`; // Empty string to act as a header
      }
      // Add the products under the header
      for (const [productKey, productDetails] of Object.entries(group.products)) {
        layout[`product: ${groupKey} ${productKey}`] = `(${productDetails.quantity})x ${productDetails.item.name}`;
      }
      return layout;
    },
    //////////////////////////////////////////////////
    // Gadget middleware API Call: Calculate Order
    ////////////////////////////////////////////////// 
    async callCalculateOrder(request) {
      const ogasysEndpoint = window.kitSelector.ogasysKitEndpoint;
      try {
        const response = await fetch(`${ogasysEndpoint}`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json' 
          },
          body: JSON.stringify(request),
        });
    
        const responseData = await response.json();
    
        return {
          status: response.status,
          ok: responseData.success,
          body: responseData.data,
        };
      } catch (error) {
        Logger.error('There was a problem with the fetch operation', error);
        throw error; // Let the caller handle the error
      }
    },
    //////////////////////////////////////////////////
    // Methods for building calculate-order request
    ////////////////////////////////////////////////// 
    async gatherCartItems() {
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.statusText}`);
        }
    
        const cartData = await response.json();
    
        if (!cartData.items || !Array.isArray(cartData.items)) {
          throw new Error(`Invalid cart data received: ${JSON.stringify(cartData)}`);
        }
    
        return cartData.items; // Return valid cart items
      } catch (error) {
        Logger.error('Error fetching cart items', error);
        throw error; // Propagate the error to the caller
      }
    },
    createGroupObject(groupKey, groupValue) {
      return {
        name: groupKey,
        items: this.addProductToGroup(groupValue.products),
        is_free: groupValue.is_free,
      };
    },
    addProductToGroup(products) {
      return Object.entries(products).map(([sku, product]) => {
        return {
          sku: product.item?.sku || sku, // fallback to key
          qty: product.quantity,
          shopify_product_variant_id: product.item?.id || null,
          additionalCount: 0,
          name: product.item?.name || sku,
        };
      });
    },
    formRequestItems(cart, newItems = []) {
      const hasCart = Array.isArray(cart) && cart.length > 0;
      const hasNewItems = Array.isArray(newItems) && newItems.length > 0;
    
      if (!hasCart && !hasNewItems) {
        Logger.warn('No cart and no new items provided. Skipping request build.');
        return null;
      }
    
      const mergedCart = hasCart ? [...cart] : [];
    
      if (hasNewItems) {
        newItems.forEach(newItem => {
          const index = mergedCart.findIndex(existing => existing.sku === newItem.sku);
    
          if (index !== -1) {
            if (this.isKit) {
              // 🔁 Replace the existing kit entirely (with new options & qty)
              mergedCart[index] = newItem;
            } else {
              // ➕ Add to quantity for regular products
              mergedCart[index].qty += newItem.qty || 1;
            }
          } else {
            mergedCart.push(newItem);
            this.newItemInCart = true;
          }
        });
      }
    
      return mergedCart;
    },  
    formatItemsFromCart(cart) {
      if (!cart || !Array.isArray(cart) || cart.length === 0) {
        return []; // Return an empty array if no cart items are found
      }

      // Map over items and transform each item into the desired structure
      return cart.map((item) => {
    
        // Ensure item properties exist
        const hasKitOptions = item.properties && item.properties['_kit_options'];
    
        // Construct the formatted item
        const formattedItem = {
          sku: item.sku,
          qty: item.quantity,
          shopify_product_variant_id: item.variant_id,
        };
    
        // Add `options` if `_kit_options` exist
        if (hasKitOptions) {
          formattedItem.options = item.properties['_kit_options'];
        }
        return formattedItem;
      });
    },
    formatNewItems(props) {
      if (!this.hasProps) return [];
    
      if (Array.isArray(props.items)) {
        return props.items.map(item => this.buildNewRequestOptions(item));
      }
    
      return [this.buildNewRequestOptions(props)];
    },
    buildNewRequestOptions(props) {
      const item = {
        sku: props.parentProductSku,
        qty: props.qty,
        shopify_product_variant_id: props.variantId
      };
      if (props.kit && Object.keys(props.kit).length > 0) {
        item.options = [];
        for (const [key, value] of Object.entries(props.kit)) {
          if (
            props.kit[key].is_free &&
            Array.isArray(props.hiddenGiftSkus) &&
            props.hiddenGiftSkus.length > 0
          ) {
            this.injectHiddenGiftsIntoOptions(props.kit[key].products, props.hiddenGiftSkus);
          }
    
          item.options.push(this.createGroupObject(key, value));
        }
      }
      return item;
    },
    injectHiddenGiftsIntoOptions(products, freeProducts) {
      if (!Array.isArray(freeProducts)) return;
    
      freeProducts.forEach(gift => {
        const { sku, quantity } = gift;
    
        // Only inject if the product doesn't already exist
        if (!products[sku]) {
          products[sku] = {
            item: {}, // item is an empty object as agreed
            quantity: quantity || 1
          };
        }
      });
    },
    
    //////////////////////////////////////////////////
    // Gadget middleware API Call: Show my price
    ////////////////////////////////////////////////// 
    async callShowMyPrice(customerId, variantId, priceElement, originalPrice) {
      if (!customerId || !variantId || !priceElement || !originalPrice) {
        Logger.error('Invalid arguments passed to callShowMyPrice', { customerId, variantId, priceElement, originalPrice });
        return;
      }
      const button = document.querySelector(`[data-price-element="${priceElement}"]`);
      if (button) {
        button.classList.add('loading');
      } 
      // Construct the formatted item
      const formattedItem = {
        ogasys_customer_id: customerId,
        shopify_product_variant_id: variantId,
        qty: 1
      };
      const result = await this.showMyPrice(formattedItem);

      if (button) {
        button.classList.remove('loading');
      }
      if (!result.ok) {
        this.updateMyPrice(originalPrice, priceElement);
        Logger.error('Error fetching price', result);
        return;
      }
      const price = result.body.netprice || result.body.price;
      this.updateMyPrice(price, priceElement);
    },
    async showMyPrice(request) {
      const ogasysEndpoint = window.kitSelector.ogasysShowPriceEndpoint;
      if (!ogasysEndpoint) {
        Logger.error('ogasysShowPriceEndpoint not defined.');
        return;
      }
      try {
        const response = await fetch(`${ogasysEndpoint}`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json' 
          },
          body: JSON.stringify(request),
        });
    
        const responseData = await response.json();
        
        return {
          status: responseData.status_code,
          ok: responseData.success,
          body: responseData.data,
        };
      } catch (error) {
        Logger.error('There was a problem with the fetch operation', error);
        throw error; // Let the caller handle the error
      }
    },
    updateMyPrice(price, priceElement) {
      const formattedPrice = price ? parseFloat(price).toFixed(2) : 'N/A'; // Fallback to 'N/A'
      if (!priceElement) {
        Logger.error('Price element not provided.');
        return;
      }
      const sectionElement = document.querySelector(`[data-price-element="${priceElement}"]`);
    
      // Ensure the section element exists
      if (sectionElement) {
        sectionElement.innerHTML = `$${formattedPrice}`;
        sectionElement.classList.add('button__my-price__gradient')
        sectionElement.classList.remove('button--icon')
      } else {
        Logger.error(`Element with selector "${priceElement}" not found.`);
      }
    }
  };
})();
